import React, { useState, useEffect, useMemo } from 'react';
import {
  useFloating,
  autoUpdate,
  offset,
  flip,
  shift,
  size,
  useClick,
  useDismiss,
  useRole,
  useInteractions,
} from '@floating-ui/react';

// Preferred dropdown width so labels stay readable even when the field lives in
// a narrow column. Clamped to the field width (min) and available space (max).
const PREFERRED_WIDTH = 320;
const MAX_HEIGHT = 440;
const MIN_HEIGHT = 180;
const VIEWPORT_PADDING = 8;

const styles = {
  wrapper: {
    position: 'relative',
    width: '100%',
    minWidth: 0,
  },
  control: {
    background: '#222',
    borderRadius: 8,
    minHeight: 38,
    color: '#fff',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '6px 12px',
    userSelect: 'none',
    border: '1px solid rgba(255,255,255,0.15)',
    minWidth: 0,
  },
  controlDisabled: {
    background: '#3B3A3A',
    cursor: 'not-allowed',
    opacity: 0.7,
  },
  controlText: {
    fontSize: 13.5,
    color: '#fff',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    flex: 1,
    minWidth: 0,
  },
  placeholder: {
    color: 'rgba(255,255,255,0.5)',
  },
  caret: {
    marginLeft: 8,
    color: 'rgba(255,255,255,0.5)',
    fontSize: 11,
    flexShrink: 0,
  },
  dropdown: {
    zIndex: 10500,
    background: '#2a2a2a',
    borderRadius: 8,
    boxShadow: '0 8px 24px rgba(0,0,0,0.45)',
    border: '1px solid rgba(255,255,255,0.08)',
    display: 'flex',
    flexDirection: 'column',
    boxSizing: 'border-box',
    outline: 'none',
  },
  searchWrap: {
    padding: 8,
    paddingBottom: 6,
    flexShrink: 0,
  },
  search: {
    background: '#333',
    borderRadius: 20,
    border: '1px solid rgba(255,255,255,.18)',
    color: '#fff',
    padding: '8px 13px',
    fontSize: 12.5,
    width: '100%',
    outline: 'none',
    boxSizing: 'border-box',
  },
  list: {
    overflowY: 'auto',
    overflowX: 'hidden',
    padding: '0 8px 8px',
    flex: 1,
    minHeight: 0,
  },
  groupHeader: {
    color: '#ef4277',
    fontSize: 10.5,
    fontWeight: 700,
    letterSpacing: '0.09em',
    textTransform: 'uppercase',
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    cursor: 'pointer',
    margin: '12px 0 4px',
    userSelect: 'none',
    minWidth: 0,
  },
  groupName: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    minWidth: 0,
  },
  groupBar: {
    display: 'inline-block',
    width: 3,
    height: 13,
    background: '#ef4277',
    borderRadius: 2,
    flexShrink: 0,
  },
  groupCount: {
    fontSize: 9,
    color: '#9a9a9c',
    marginLeft: 4,
    flexShrink: 0,
  },
  groupCaret: {
    marginLeft: 'auto',
    color: '#ef4277',
    fontSize: 11,
    transition: 'transform 0.15s',
    flexShrink: 0,
  },
  item: {
    padding: '7px 10px',
    borderRadius: 8,
    fontSize: 13.5,
    cursor: 'pointer',
    color: '#fff',
    marginBottom: 1,
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    minWidth: 0,
  },
  itemLabel: {
    flex: 1,
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  itemSelected: {
    background: '#ef4277',
  },
  empty: {
    color: 'rgba(255,255,255,0.4)',
    fontSize: 13,
    padding: '8px 10px',
  },
};

// Only live items are shown. Items that are not live yet ('soon' / 'request') are
// hidden, and so is any group left without live items. Item definitions stay in the
// picker group builders, so an item shows up as soon as it is marked live.
const getLiveGroups = (groups) =>
  groups
    .map(group => ({ ...group, items: group.items.filter(item => item.status === 'live') }))
    .filter(group => group.items.length > 0);

const GroupedPicker = ({
  groups: allGroups = [],
  selectedLabel = null,
  placeholder = '- Select -',
  isDisabled = false,
  onSelect,
}) => {
  const groups = useMemo(() => getLiveGroups(allGroups), [allGroups]);
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [floatSize, setFloatSize] = useState({ width: undefined, maxHeight: MAX_HEIGHT });
  const [expandedGroups, setExpandedGroups] = useState(() => {
    const init = {};
    groups.forEach((g, i) => { init[i] = false; });
    return init;
  });

  const { refs, floatingStyles, context } = useFloating({
    open: isOpen,
    onOpenChange: (open) => {
      if (open && isDisabled) return;
      if (open) {
        // Expand the group containing the current selection.
        if (selectedLabel) {
          const selectedGroupIndex = groups.findIndex(g =>
            g.items.some(item => item.label === selectedLabel && item.status === 'live')
          );
          if (selectedGroupIndex !== -1) {
            setExpandedGroups(prev => {
              const next = {};
              Object.keys(prev).forEach(k => { next[k] = false; });
              next[selectedGroupIndex] = true;
              return next;
            });
          }
        }
      } else {
        setSearch('');
      }
      setIsOpen(open);
    },
    placement: 'bottom-start',
    strategy: 'fixed',
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(4),
      flip({ padding: VIEWPORT_PADDING }),
      shift({ padding: VIEWPORT_PADDING }),
      size({
        padding: VIEWPORT_PADDING,
        apply({ availableHeight, availableWidth, rects }) {
          const preferred = Math.max(rects.reference.width, PREFERRED_WIDTH);
          const width = Math.min(preferred, Math.max(rects.reference.width, availableWidth));
          const maxHeight = Math.max(MIN_HEIGHT, Math.min(MAX_HEIGHT, availableHeight));
          setFloatSize(prev =>
            prev.width === width && prev.maxHeight === maxHeight ? prev : { width, maxHeight }
          );
        },
      }),
    ],
  });

  const click = useClick(context, { enabled: !isDisabled });
  const dismiss = useDismiss(context);
  const role = useRole(context, { role: 'listbox' });
  const { getReferenceProps, getFloatingProps } = useInteractions([click, dismiss, role]);

  // Keep expanded-group state in sync when the set of groups changes.
  useEffect(() => {
    setExpandedGroups(prev => {
      const next = {};
      groups.forEach((g, i) => { next[i] = i in prev ? prev[i] : false; });
      return next;
    });
  }, [groups.length]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleGroup = (index) => {
    setExpandedGroups(prev => {
      const wasOpen = prev[index];
      const next = {};
      Object.keys(prev).forEach(k => { next[k] = false; });
      next[index] = !wasOpen;
      return next;
    });
  };

  const lowerSearch = search.toLowerCase().trim();
  const isSearchActive = lowerSearch.length > 0;

  const filteredGroups = groups.map((group, i) => {
    const filteredItems = lowerSearch
      ? group.items.filter(item => item.label.toLowerCase().includes(lowerSearch))
      : group.items;
    return { ...group, items: filteredItems, originalIndex: i };
  }).filter(g => g.items.length > 0);

  const handleItemClick = (item) => {
    if (onSelect) onSelect(item.data);
    setIsOpen(false);
    setSearch('');
  };

  return (
    <div style={styles.wrapper}>
      <div
        ref={refs.setReference}
        style={{ ...styles.control, ...(isDisabled ? styles.controlDisabled : {}) }}
        {...getReferenceProps()}
      >
        <span style={{ ...styles.controlText, ...(selectedLabel ? {} : styles.placeholder) }}>
          {selectedLabel || placeholder}
        </span>
        <span style={styles.caret}>{isOpen ? '▲' : '▼'}</span>
      </div>

      {isOpen && (
        <div
          ref={refs.setFloating}
          style={{
            ...floatingStyles,
            ...styles.dropdown,
            width: floatSize.width,
            maxHeight: floatSize.maxHeight,
          }}
          {...getFloatingProps()}
        >
          <div style={styles.searchWrap}>
            <input
              style={styles.search}
              placeholder="Search..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              autoFocus
            />
          </div>

          <div style={styles.list}>
            {filteredGroups.map((group) => {
              const idx = group.originalIndex;
              const isExpanded = isSearchActive || expandedGroups[idx];
              return (
                <div key={idx}>
                  <div style={styles.groupHeader} onClick={() => !isSearchActive && toggleGroup(idx)}>
                    <span style={styles.groupBar} />
                    <span style={styles.groupName}>{group.name}</span>
                    <span style={styles.groupCount}>({group.items.length})</span>
                    <span style={{ ...styles.groupCaret, transform: isExpanded ? 'rotate(90deg)' : 'none' }}>
                      &#9654;
                    </span>
                  </div>
                  {isExpanded && group.items.map((item, itemIdx) => {
                    const isSelected = item.label === selectedLabel;

                    return (
                      <div
                        key={itemIdx}
                        style={{
                          ...styles.item,
                          ...(isSelected ? styles.itemSelected : {}),
                        }}
                        title={item.hint || undefined}
                        onClick={() => handleItemClick(item)}
                        onMouseEnter={e => {
                          if (!isSelected) {
                            e.currentTarget.style.background = 'rgba(255,255,255,.07)';
                          }
                        }}
                        onMouseLeave={e => {
                          if (!isSelected) {
                            e.currentTarget.style.background = 'transparent';
                          }
                        }}
                      >
                        <span style={styles.itemLabel}>{item.label}</span>
                      </div>
                    );
                  })}
                </div>
              );
            })}

            {filteredGroups.length === 0 && (
              <div style={styles.empty}>No results found</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default GroupedPicker;
